<template>
  <li
    class="group text-sm leading-tight flex flex-1 items-center divide-x hover:text-blue-800 transition even:bg-gray-300/15 divide-gray-200 text-gray-700"
  >
    <s-link
      :href="route('task-response-attachments.show', [attachment.id])"
      target="_blank"
      external
      class="link-subtle hover:bg-blue-200/15 transition flex-1 min-w-0 w-full flex justify-between gap-1 truncate px-3 py-2 focus-visible:outline-blue-400 -outline-offset-2"
      :aria-label="t('aria.download', {filename: attachment.filename})"
    >
      <span class="flex-1 min-w-0 truncate">{{ attachment.filename }}</span>
      <span class="transition flex items-center gap-1">
        <s-icon name="download" class="opacity-70" size="16" />
        {{ t('actions.download') }}
      </span>
    </s-link>
    <button
      v-if="previewSupported"
      @click.prevent="viewAttachment(attachment)"
      class="hover:bg-blue-200/15 transition link-subtle flex items-center justify-center gap-1 px-3 py-2 min-w-[5rem] focus-visible:outline-blue-400 -outline-offset-2"
      :aria-label="t('aria.view', {filename: attachment.filename})"
    >
      <s-icon name="eye" class="opacity-70" size="16" />
      {{ t('view') }}
    </button>
    <button
      v-if="deletable"
      @click.prevent="deleteAttachment()"
      class="hover:bg-blue-200/15 transition link-subtle flex items-center gap-1 px-3 py-2 focus-visible:outline-blue-400 -outline-offset-2"
      :disabled="deleting"
      :class="{
        'cursor-progress': deleting,
      }"
      :aria-label="t('aria.delete', {filename: attachment.filename})"
    >
      <s-icon v-if="deleting" name="loading" class="opacity-70 animate-spin" size="16" />
      <s-icon v-else name="trash-can" class="opacity-70" size="16" />
      {{ t('actions.delete') }}
    </button>
    <span role="alert" class="sr-only">
      <template v-if="deleting">
        {{ t('alerts.deleting', {filename: attachment.filename}) }}
      </template>
    </span>
    <s-modal
      v-if="attachmentUrl && (isImage || isPdf)"
      v-model:open="showModal"
      :title="attachment.filename || t('modal.title')"
      width="md"
    >
      <template #content>
        <a
          :href="attachmentUrl"
          target="_blank"
          class="grid grid-rows-1 grid-cols-1 card border border-gray-200 cursor-pointer"
        >
          <div class="p-4 bg-gray-150 col-span-full row-span-full">
            <div class="w-full h-full flex-1 flex items-center justify-center">
              <div class="loader"></div>
            </div>
          </div>
          <img
            v-if="isImage"
            :src="attachmentUrl"
            role="presentation"
            class="min-w-full w-full h-auto relative top-0 col-span-full row-span-full"
          />
          <object
            v-if="isPdf"
            :key="activeAttachmentKey"
            :data="attachmentUrl"
            style="aspect-ratio: 8.5 / 11"
            class="min-w-full w-full relative top-0 col-span-full row-span-full"
          ></object>
        </a>
      </template>
    </s-modal>
  </li>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js';
import SIcon from '../design-system/SIcon.vue';
import SLink from './SLink.vue';
import {inlineViewableImageMimeTypes, inlineViewableImageTypeExtensions} from '../util/images';
import {router} from '@inertiajs/vue3';
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import SModal from '../design-system/SModal.vue';

const {attachment, deletable, onlyOnDelete} = defineProps<{
  attachment: {
    id?: number;
    filename: string;
    mimeType?: string | null;
  };
  deletable: boolean;
  onlyOnDelete: string[];
}>();

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});

const deleting = ref(false);

const supportsPreviewMimeTypes = [...inlineViewableImageMimeTypes, 'application/pdf'];
const supportsPreviewExtensions = [...inlineViewableImageTypeExtensions, 'pdf'];

const previewSupported = computed(() => {
  if (attachment.mimeType) {
    return supportsPreviewMimeTypes.includes(attachment.mimeType);
  }

  const ext = attachment.filename.split('.').pop();

  return ext && supportsPreviewExtensions.includes(ext.toLowerCase());
});

const deleteAttachment = () => {
  router.delete(route('task-response-attachments.destroy', [attachment.id]), {
    only: onlyOnDelete,
    preserveState: true,
    onStart() {
      deleting.value = true;
    },
    onFinish() {
      deleting.value = false;
    },
  });
};

const showModal = ref(false);
const attachmentUrl = ref('');
const attachmentExtension = computed(() => {
  const extension = attachment.filename.split('.').reverse().shift() || '';

  return extension.toLowerCase();
});

const viewAttachment = (attachment: any) => {
  // Reset these values.
  showModal.value = false;
  attachmentUrl.value = '';

  if (!attachment) {
    return;
  }

  // Because Safari won't follow a redirect in an object tag, we need to handle PDF viewing differently for Safari.
  // This is slower for the user, which is why we selectively look for this case before using this hack.
  const currentlyInSafari =
    navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');

  if (isPdf.value && currentlyInSafari) {
    getSignedUrlFromApiAndLoadAttachmentModal(attachment.id);
  } else {
    attachmentUrl.value = route('task-response-attachments.show', {
      attachment: attachment.id,
      inline: '1',
    });
    showModal.value = true;
  }
};

const activeAttachmentKey = computed(() => {
  return typeof attachment.id === 'number' ? `attachment-${attachment.id}` : 'no-attachment';
});

/**
 * This function will call our API to get the signed URL for the attachment.
 * We then use that to display the attachment in a modal.
 * @param attachmentId
 */
const getSignedUrlFromApiAndLoadAttachmentModal = (attachmentId: number) => {
  fetch(
    route('api.v1.task-response-attachments.signed-url', {
      attachment: attachmentId,
      inline: '1',
    })
  )
    .then((response) => response.json())
    .then((data) => {
      attachmentUrl.value = data.signedUrl;
      showModal.value = true;
    });
};

const isImage = computed(() => {
  if (attachment.mimeType) {
    return inlineViewableImageMimeTypes.includes(attachment.mimeType);
  }

  // Fall back to extensions if we don't have a mime type saved.
  return inlineViewableImageTypeExtensions.includes(attachmentExtension.value);
});

const isPdf = computed(() => {
  if (attachment.mimeType) {
    return attachment.mimeType === 'application/pdf';
  }

  // Fallback to extension check
  return attachmentExtension.value === 'pdf';
});
</script>
<i18n>
{
  "en": {
    "view": "View",
    "alerts.deleting": "Deleting attachment: {filename}.",
    "aria.delete": "Delete attachment: {filename}.",
    "aria.download": "Download attachment: {filename}.",
    "aria.view": "View attachment: {filename}.",
    "modal.title": "File Viewer"
  },
  "fr": {
    "view": "Voir",
    "alerts.deleting": "Suppression de la pièce jointe : {filename}.",
    "aria.delete": "Supprimer la pièce jointe : {filename}.",
    "aria.download": "Télécharger la pièce jointe : {filename}.",
    "aria.view": "Voir la pièce jointe : {filename}.",
    "modal.title": "Visionneuse de fichiers"
  }
}
</i18n>
